import { ReactDayPickerRange } from '@/components/calendar/ReactDayPickerRange';
import { useStore } from '@/hooks/useStore';
import { cn } from '@/utils/utils';
import { observer } from 'mobx-react-lite';
import { useState, Children, useEffect, cloneElement } from 'react';
import { addMonths, isBefore, isAfter, isSameDay } from 'date-fns';
import { NavProps } from 'react-day-picker';
import { toJS } from 'mobx';
import moment from 'moment';
import { DATE_TIME_ISO_NO_TIMEZONE } from '@/configs/constants';

export default observer(function CalendarSelectBike() {
    // #region store
    const {
        bookingStore: { bikeBookingDetail, modifiers, setObservable }
    } = useStore();
    // #endregion

    // #region state
    const nowInPlus9 = moment().utcOffset(9 * 60);

    const limitMonth = addMonths(new Date(), 6); // Tính tháng giới hạn (hiện tại + 6 tháng)

    const [fromMonth, setFromMonth] = useState(new Date());
    const [earliestMonth, setEarliestmonth] = useState(addMonths(new Date(), -1).getMonth());
    const [disableDates, setDisableDates] = useState([
        { before: new Date() },
        { after: limitMonth }
    ]);

    const toMonth = addMonths(fromMonth, 1);
    // #endregion

    // #region lifecycle
    useEffect(() => {
        if (bikeBookingDetail) {
            const res = toJS(bikeBookingDetail);
            let datesWithStatus = [] as any;
            let disableDates = [] as any[];

            let dayPickerModifiers: Record<string, Date[]> = {} as Record<string, Date[]>;
            if (res.nonRentalDatesDetails && res?.nonRentalDatesDetails.length > 0) {
                res?.nonRentalDatesDetails.forEach((data) => {
                    datesWithStatus.push({
                        date: new Date(data),
                        status: 'blocked'
                    });
                });
            }

            if (res.timeHasBeenBooking && res.timeHasBeenBooking.length > 0) {
                res.timeHasBeenBooking.forEach((data) => {
                    const rentalFromJST = moment.utc(data.rentalFrom).add(9, 'hours').format(DATE_TIME_ISO_NO_TIMEZONE);
                    const rentalToJST = moment.utc(data.rentalTo).add(9, 'hours').format(DATE_TIME_ISO_NO_TIMEZONE);

                    datesWithStatus.push({
                        range: {
                            from: new Date(rentalFromJST),
                            to: new Date(rentalToJST)
                        },
                        status: 'booked'
                    });
                });
            }

            if (datesWithStatus.length > 0) {
                dayPickerModifiers = {
                    ...dayPickerModifiers, ...datesWithStatus.reduce(
                        (acc, { date, status, range }) => {
                            if (status === 'blocked') {
                                acc[status] = acc[status] || [];
                                acc[status].push(date);
                                acc.disabled = [...(acc.disabled || []), date];
                            } else if (status === 'booked' && range) {
                                acc[status] = acc[status] || [];
                                acc[status].push(range); // Lưu range vào mảng booked
                            }
                            return acc;
                        },
                        { booked: [], blocked: [], disabled: [] } as Record<string, Date[]>
                    )
                };
                disableDates.push(...[...datesWithStatus.filter(date => date.status === 'blocked').map(item => ({ date: item.date }))]);
                // setDisableDates(prev => [...prev, ...datesWithStatus.filter(date => date.status === 'blocked').map(item => ({ date: item.date }))]);
            }

            if (res.bookingLeadTime && !Number.isNaN(Number(res.bookingLeadTime))) {
                const earliest = nowInPlus9
                    .clone()
                    .add(Number(res.bookingLeadTime), 'hours');
                // .startOf('day');

                if (earliest.minutes() > 0) {
                    earliest.add(1, 'hour').startOf('hour');
                } else {
                    earliest.startOf('hour');
                }

                const year = earliest.year(); // ví dụ 2025
                const month = earliest.month(); // 0–11, tức 0 = Jan
                const day = earliest.date(); // 1–31

                // 3. Tạo Date local 00:00 cho calendar
                const onlyDate = new Date(year, month, day);

                disableDates.push(...[{ before: onlyDate }, { after: limitMonth }]);
                // setDisableDates([
                //     { before: onlyDate },
                //     { after: limitMonth }
                // ]);
                setEarliestmonth(onlyDate.getMonth());
            }

            if (disableDates.length > 0) {
                setDisableDates(prev => [...prev, ...disableDates]);
            }

            setObservable('modifiers', dayPickerModifiers);
        }
    }, [bikeBookingDetail]);
    // #endregion

    // #region function
    const generateModifiers = (date: Date) => {
        let status = '';

        // Kiểm tra nếu ngày nằm trong phạm vi của blocked
        const { blocked, booked } = modifiers;
        if (blocked?.some(d => isSameDay(d, date))) {
            status = 'blocked';
        }

        // Kiểm tra nếu ngày nằm trong phạm vi của booked
        if (booked?.some(range => isInBookedRange(date, range))) {
            status = 'booked';
        }

        return status;
    };

    const isInBookedRange = (date: Date, range: { from: Date, to: Date }) => {
        return (isAfter(date, range?.from) && isBefore(date, range?.to)) || isSameDay(date, range?.from) || isSameDay(date, range?.to);
    };

    // Handler khi chuyển tháng bên phải
    const handleNextMonth = () => {
        setFromMonth(prev => addMonths(prev, 1));
    };

    return (
        <div>
            <div className='w-full xl:grid xl:grid-cols-2 gap-4 mb-5'>
                <div className='xl:block hidden'>
                    <ReactDayPickerRange
                        mode='range'
                        className='max-w-full !pt-4 !pb-6 xsm:!px-6 xsm:!pt-4 xsm:!pb-6'
                        classNames={{
                            months: 'relative flex flex-col h-full'
                        }}
                        showOutsideDays={false}
                        month={fromMonth}
                        onMonthChange={setFromMonth}
                        modifiers={{
                            booked: (date: Date) => generateModifiers(date) === 'booked',
                            blocked: (date: Date) => generateModifiers(date) === 'blocked'
                        }}
                        modifiersClassNames={{
                            booked: 'day-booked',
                            blocked: 'day-blocked'
                        }}
                        disabled={disableDates}
                        disabledSelect={true}
                        disableHoverday={true}
                        fixedWeeks
                        captionLayout='label'
                        components={{
                            Nav: ({ children, className, ...props }: NavProps) => {
                                const [previousButton] = Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;
                                const prevWithClass = cloneElement(previousButton, {
                                    disabled: fromMonth.getMonth() <= earliestMonth && fromMonth.getFullYear() === (new Date()).getFullYear()
                                });
                                return (
                                    <nav className={cn('flex items-center min-h-6 justify-between', className)} {...props}>
                                        <div className='absolute top-1/2 -left-10'>{prevWithClass}</div>
                                        <div></div>
                                    </nav>
                                );
                            }
                        }}

                    />
                </div>
                <div className='xl:block hidden'>
                    <ReactDayPickerRange
                        mode='range'
                        className='max-w-full !pt-4 !pb-6 xsm:!px-6 xsm:!pt-4 xsm:!pb-6'
                        classNames={{
                            months: 'relative flex flex-col h-full'
                        }}
                        showOutsideDays={false}
                        month={toMonth}
                        onMonthChange={handleNextMonth}
                        modifiers={{
                            booked: (date: Date) => generateModifiers(date) === 'booked',
                            blocked: (date: Date) => generateModifiers(date) === 'blocked'
                        }}
                        modifiersClassNames={{
                            booked: 'day-booked',
                            blocked: 'day-blocked'
                        }}
                        disabled={disableDates}
                        disabledSelect={true}
                        disableHoverday={true}
                        fixedWeeks
                        captionLayout='label'
                        components={{
                            Nav: ({ children, className, ...props }: NavProps) => {
                                const [, nextButton] = Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;

                                const nextWithClass = cloneElement(nextButton, {
                                    disabled: isAfter(toMonth, limitMonth) || (toMonth.getFullYear() === limitMonth.getFullYear() && toMonth.getMonth() >= limitMonth.getMonth())
                                });
                                return (
                                    <nav className={cn('flex items-center min-h-6 justify-between', className)} {...props}>
                                        <div></div>
                                        <div className='absolute top-1/2 -right-10'>{nextWithClass}</div>
                                    </nav>
                                );
                            }
                        }}

                    />
                </div>
                <div className='xl:hidden'>
                    <ReactDayPickerRange
                        mode='range'
                        className='max-w-full w-full !px-3 !pt-4 !pb-4 xsm:!px-3 xsm:!pt-4 xsm:!pb-6'
                        showOutsideDays={false}
                        month={fromMonth}
                        onMonthChange={setFromMonth}
                        modifiers={{
                            booked: (date: Date) => generateModifiers(date) === 'booked',
                            blocked: (date: Date) => generateModifiers(date) === 'blocked'
                        }}
                        modifiersClassNames={{
                            booked: 'day-booked',
                            blocked: 'day-blocked'
                        }}
                        disabled={disableDates}
                        disabledSelect={true}
                        disableHoverday={true}
                        fixedWeeks
                        captionLayout='label'
                        components={{
                            Nav: ({ children, className, ...props }: NavProps) => {
                                const [previousButton, nextButton] = Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;
                                const prevWithClass = cloneElement(previousButton, {
                                    disabled: fromMonth.getMonth() <= earliestMonth && fromMonth.getFullYear() === (new Date()).getFullYear()
                                });

                                const nextWithClass = cloneElement(nextButton, {
                                    disabled: isAfter(fromMonth, limitMonth) || (fromMonth.getFullYear() === limitMonth.getFullYear() && fromMonth.getMonth() >= limitMonth.getMonth())
                                });

                                return (
                                    <nav className={cn('flex items-center min-h-8 justify-between', className)} {...props}>
                                        {prevWithClass}
                                        {nextWithClass}
                                    </nav>
                                );
                            }
                        }}
                    />
                </div>
            </div>

            <div className='flex flex-col gap-0.5 justify-center items-center md:items-start text-[12px] leading-[20px]'>
                <p className='item-des-blocked ml-[16px]'>
                    バイクのご利用が不可の日（点検など）
                </p>
                <p className='item-des-booked ml-[16px]'>
                    既に他の方が予約済みの日
                </p>
            </div>

        </div>
    );
});
